/**
 * RegisterSensorModal — Phase 10 redesign.
 *
 * The operator picks ONE of three sensor models and we route the form
 * accordingly:
 *
 *   DEDRONE          → creates a Dedrone integration (POST /integrations/dedrone).
 *                       No sensor row is created here; the Dedrone poller
 *                       auto-discovers the sub-sensors on its first run.
 *
 *   DRONETAG         → creates a sensor row of type RF_DETECTOR with
 *                       dataFormat DUMP and the Dronetag Sensor ID stored
 *                       in configuration JSONB.
 *
 *   GENERIC_REMOTE_ID → creates a plain sensor row of type RF_DETECTOR
 *                       with dataFormat JSON_CUSTOM. Catch-all for any
 *                       ASTM F3411 / Remote ID broadcast source.
 *
 * Location: hidden for Dedrone (the poller backfills sensor coords from
 * /api/1.0/systemstate) and Dronetag (the Scout broadcasts coords in the
 * DUMP webhook payload). For Generic Remote ID we keep an optional
 * collapsed block — operators can paste fixed coords for stationary
 * receivers if they want.
 */
import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { SensorType, DataFormat } from '@airrecon/shared';
import { sensorsApi } from '../../services/api';
import { dedroneApi } from '../../services/dedroneApi';
import {
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon,
  SignalIcon,
  RadioIcon,
} from '@heroicons/react/24/outline';

// ── Sensor models (UI concept) ─────────────────────────────────────────────────

type SensorModel = 'DEDRONE' | 'DRONETAG' | 'GENERIC_REMOTE_ID';

interface ModelDef {
  id: SensorModel;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const SENSOR_MODELS: ModelDef[] = [
  {
    id: 'DEDRONE',
    label: 'DeDrone',
    description:
      'DroneTracker server. Polls /api/1.0/systemstate and receives detection webhooks. Location auto-detected from the server.',
    icon: ShieldCheckIcon,
  },
  {
    id: 'DRONETAG',
    label: 'Dronetag',
    description:
      'Dronetag Scout receiver. Forwards DUMP webhooks for every Remote ID broadcast it hears. Location auto-detected from the Scout.',
    icon: SignalIcon,
  },
  {
    id: 'GENERIC_REMOTE_ID',
    label: 'Generic Remote ID',
    description:
      'Any ASTM F3411 / Remote ID feed that posts JSON to the ingestion endpoint. Use for receivers not covered by the two above.',
    icon: RadioIcon,
  },
];

// ── Component ──────────────────────────────────────────────────────────────────

interface RegisterSensorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CreatedDedroneResult {
  kind: 'dedrone';
  integration: {
    id: string;
    label: string;
    webhookUrl: string;
  };
}

interface CreatedSensorResult {
  kind: 'sensor';
  sensor: any;
}

type CreatedResult = CreatedDedroneResult | CreatedSensorResult;

export default function RegisterSensorModal({ isOpen, onClose }: RegisterSensorModalProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [createdResult, setCreatedResult] = useState<CreatedResult | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const [model, setModel] = useState<SensorModel | null>(null);

  // Common
  const [name, setName] = useState('');

  // Dedrone fields
  const [dedroneBaseUrl, setDedroneBaseUrl] = useState('');
  const [dedroneApiToken, setDedroneApiToken] = useState('');
  const [dedronePollSeconds, setDedronePollSeconds] = useState(60);

  // Dronetag fields
  const [dronetagSensorId, setDronetagSensorId] = useState('');

  // Generic Remote ID fields (optional location)
  const [showLocation, setShowLocation] = useState(false);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [altitudeMSL, setAltitudeMSL] = useState('');

  const [error, setError] = useState<string | null>(null);

  function resetForm() {
    setModel(null);
    setName('');
    setDedroneBaseUrl('');
    setDedroneApiToken('');
    setDedronePollSeconds(60);
    setDronetagSensorId('');
    setShowLocation(false);
    setLatitude('');
    setLongitude('');
    setAltitudeMSL('');
    setError(null);
    setCreatedResult(null);
    setCopiedField(null);
  }

  function handleClose() {
    if (submitting) return;
    resetForm();
    onClose();
  }

  function copyToClipboard(value: string, field: string) {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }

  function validateLocation(): boolean {
    if (!showLocation) return true;
    if (!latitude && !longitude) return true; // both blank = skip
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    if (Number.isNaN(lat) || lat < -90 || lat > 90) {
      setError('Latitude must be between -90 and 90');
      return false;
    }
    if (Number.isNaN(lon) || lon < -180 || lon > 180) {
      setError('Longitude must be between -180 and 180');
      return false;
    }
    return true;
  }

  async function submitDedrone() {
    if (!dedroneBaseUrl.trim()) {
      setError('DroneTracker URL is required');
      return;
    }
    if (!/^https?:\/\//.test(dedroneBaseUrl.trim())) {
      setError('URL must start with http:// or https://');
      return;
    }
    if (!dedroneApiToken.trim()) {
      setError('Dedrone-Auth-Token is required');
      return;
    }
    const created = await dedroneApi.create({
      label: name.trim() || undefined,
      baseUrl: dedroneBaseUrl.trim().replace(/\/+$/, ''),
      apiToken: dedroneApiToken.trim(),
      pollIntervalSeconds: dedronePollSeconds,
      enabled: true,
    });
    setCreatedResult({
      kind: 'dedrone',
      integration: {
        id: created.id,
        label: created.label,
        webhookUrl: created.webhookUrl,
      },
    });
    queryClient.invalidateQueries({ queryKey: ['dedrone-integrations'] });
  }

  async function submitSensor(format: DataFormat, configuration?: Record<string, unknown>) {
    if (!name.trim()) {
      setError('Sensor name is required');
      return;
    }
    const payload: {
      name: string;
      type: SensorType;
      dataFormat: DataFormat;
      configuration?: Record<string, unknown>;
      location?: { latitude: number; longitude: number; altitudeMSL?: number };
    } = {
      name: name.trim(),
      // Both Dronetag and Generic Remote ID land as RF_DETECTOR under the
      // hood — the user picks "what they bought", the platform classifies
      // it at the sensor-type level.
      type: SensorType.RF_DETECTOR,
      dataFormat: format,
    };
    if (configuration) payload.configuration = configuration;
    if (showLocation && latitude && longitude) {
      payload.location = {
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        ...(altitudeMSL ? { altitudeMSL: parseFloat(altitudeMSL) } : {}),
      };
    }
    const response = await sensorsApi.create(payload);
    setCreatedResult({ kind: 'sensor', sensor: response.data || response });
    queryClient.invalidateQueries({ queryKey: ['sensors'] });
  }

  async function onSubmit() {
    if (!model) return;
    setError(null);
    if (!validateLocation()) return;
    setSubmitting(true);
    try {
      if (model === 'DEDRONE') {
        await submitDedrone();
      } else if (model === 'DRONETAG') {
        if (!dronetagSensorId.trim()) {
          setError('Dronetag Sensor ID is required');
          setSubmitting(false);
          return;
        }
        await submitSensor(DataFormat.DUMP, {
          dronetag_sensor_id: dronetagSensorId.trim(),
        });
      } else if (model === 'GENERIC_REMOTE_ID') {
        await submitSensor(DataFormat.JSON_CUSTOM);
      }
      toast.success(model === 'DEDRONE' ? 'Dedrone integration created' : 'Sensor registered');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Registration failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const inputClasses =
    'w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent text-sm';
  const labelClasses = 'block text-xs font-medium text-gray-400 mb-1.5';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl rounded-xl bg-navy-800 border border-navy-700 p-6 shadow-2xl">
                {createdResult ? (
                  <SuccessView
                    result={createdResult}
                    onClose={handleClose}
                    onCopy={copyToClipboard}
                    copiedField={copiedField}
                    onGoToSensor={(id) => {
                      handleClose();
                      navigate(`/sensors/${id}`);
                    }}
                  />
                ) : (
                  /* ── Registration Form ── */
                  <>
                    <div className="flex items-center justify-between mb-5">
                      <Dialog.Title className="text-lg font-semibold text-white">
                        {model ? `Add ${SENSOR_MODELS.find((m) => m.id === model)?.label}` : 'Add a Sensor'}
                      </Dialog.Title>
                      <button
                        onClick={handleClose}
                        className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-navy-700 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Model picker — three radio-style cards */}
                    {!model && (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-400">
                          Pick which type of sensor or integration you want to add.
                        </p>
                        {SENSOR_MODELS.map((m) => {
                          const Icon = m.icon;
                          return (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => setModel(m.id)}
                              className="w-full text-left p-4 rounded-lg border border-navy-700 hover:border-accent/50 hover:bg-navy-700/40 transition-colors group"
                            >
                              <div className="flex items-start gap-3">
                                <div className="p-2 rounded-lg bg-accent/10 text-accent">
                                  <Icon className="h-5 w-5" />
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-semibold text-white group-hover:text-accent transition-colors">
                                    {m.label}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                                    {m.description}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Model-specific form */}
                    {model && (
                      <div className="space-y-4">
                        {/* Name — universal */}
                        <div>
                          <label className={labelClasses}>
                            {model === 'DEDRONE' ? 'Label (optional)' : 'Sensor Name'}{' '}
                            {model !== 'DEDRONE' && <span className="text-alert">*</span>}
                          </label>
                          <input
                            type="text"
                            placeholder={
                              model === 'DEDRONE'
                                ? 'HQ Tracker'
                                : model === 'DRONETAG'
                                  ? 'Bridge Scout 1'
                                  : 'Roof RID Receiver'
                            }
                            className={inputClasses}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                          />
                          {model === 'DEDRONE' && (
                            <p className="mt-1 text-[11px] text-gray-500">
                              Used in the Sensors list to distinguish DroneTracker servers. Defaults to the host name.
                            </p>
                          )}
                        </div>

                        {/* Dedrone-specific */}
                        {model === 'DEDRONE' && (
                          <>
                            <div>
                              <label className={labelClasses}>
                                DroneTracker URL <span className="text-alert">*</span>
                              </label>
                              <input
                                type="url"
                                placeholder="https://dronetracker.example.com"
                                className={inputClasses}
                                value={dedroneBaseUrl}
                                onChange={(e) => setDedroneBaseUrl(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className={labelClasses}>
                                Dedrone-Auth-Token <span className="text-alert">*</span>
                              </label>
                              <input
                                type="password"
                                placeholder="paste token here"
                                className={`${inputClasses} font-mono`}
                                value={dedroneApiToken}
                                onChange={(e) => setDedroneApiToken(e.target.value)}
                              />
                              <p className="mt-1 text-[11px] text-gray-500">
                                Generate in DedroneTracker.AI: top-right user menu →{' '}
                                <strong>Profile page</strong> → <strong>API access</strong>{' '}
                                → <strong>[Create]</strong>. Stored encrypted here, never
                                displayed back.
                              </p>
                            </div>
                            <div>
                              <label className={labelClasses}>Poll Interval (seconds)</label>
                              <input
                                type="number"
                                min={15}
                                max={3600}
                                className={inputClasses}
                                value={dedronePollSeconds}
                                onChange={(e) => setDedronePollSeconds(parseInt(e.target.value, 10) || 60)}
                              />
                              <p className="mt-1 text-[11px] text-gray-500">
                                How often we refresh sensor health (15–3600). Sub-sensors are auto-discovered and their locations come from the server.
                              </p>
                            </div>
                          </>
                        )}

                        {/* Dronetag-specific */}
                        {model === 'DRONETAG' && (
                          <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
                            <label className={labelClasses}>
                              Dronetag Sensor ID <span className="text-alert">*</span>
                            </label>
                            <input
                              type="text"
                              placeholder="e.g. scout-a1b2c3d4"
                              className={inputClasses}
                              autoComplete="off"
                              spellCheck={false}
                              value={dronetagSensorId}
                              onChange={(e) => setDronetagSensorId(e.target.value)}
                            />
                            <p className="mt-2 text-[11px] leading-relaxed text-gray-400">
                              Find this in the{' '}
                              <a
                                href="https://integrations.dronetag.app/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent hover:underline"
                              >
                                Dronetag Integration Portal
                              </a>{' '}
                              under your Scout — it's the identifier Dronetag sends in the{' '}
                              <code className="text-accent">sensor_id</code> field of every DUMP message.
                              Location is broadcast by the Scout itself, so we'll fill it in on the first webhook.
                            </p>
                          </div>
                        )}

                        {/* Generic Remote ID-specific (just a hint — no required fields) */}
                        {model === 'GENERIC_REMOTE_ID' && (
                          <div className="p-3 bg-iron-800/50 border border-iron-700 rounded-lg text-[11px] text-gray-400 leading-relaxed">
                            After registration you'll get an API key. POST ASTM F3411 / Remote ID payloads
                            to the JSON ingestion endpoint with{' '}
                            <code className="text-accent">Authorization: Bearer &lt;key&gt;</code>.
                            Each payload's lat/lon is used per-detection, so this sensor doesn't need a fixed location
                            unless it's a stationary receiver — toggle below if so.
                          </div>
                        )}

                        {/* Location override — only for Generic Remote ID */}
                        {model === 'GENERIC_REMOTE_ID' && (
                          <div>
                            <button
                              type="button"
                              onClick={() => setShowLocation((v) => !v)}
                              className="text-[12px] text-accent hover:text-accent-light"
                            >
                              {showLocation ? '− Hide' : '+ Set'} a fixed receiver location
                            </button>
                            {showLocation && (
                              <div className="grid grid-cols-3 gap-3 mt-2">
                                <div>
                                  <label className="block text-[10px] text-gray-500 mb-1">Latitude</label>
                                  <input
                                    type="number" step="any" placeholder="33.9425"
                                    className={inputClasses}
                                    value={latitude} onChange={(e) => setLatitude(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] text-gray-500 mb-1">Longitude</label>
                                  <input
                                    type="number" step="any" placeholder="-118.4081"
                                    className={inputClasses}
                                    value={longitude} onChange={(e) => setLongitude(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] text-gray-500 mb-1">Alt (m MSL)</label>
                                  <input
                                    type="number" step="any" placeholder="38"
                                    className={inputClasses}
                                    value={altitudeMSL} onChange={(e) => setAltitudeMSL(e.target.value)}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {error && (
                          <div className="flex items-start gap-2 p-3 rounded-lg bg-alert/10 border border-alert/30 text-alert text-xs">
                            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-between gap-3 pt-2 border-t border-navy-700/50">
                          <button
                            type="button"
                            onClick={() => setModel(null)}
                            className="text-[12px] text-gray-400 hover:text-white"
                            disabled={submitting}
                          >
                            ← Change type
                          </button>
                          <div className="flex gap-2">
                            <button type="button" onClick={handleClose} className="btn-secondary" disabled={submitting}>
                              Cancel
                            </button>
                            <button type="button" onClick={onSubmit} className="btn-primary" disabled={submitting}>
                              {submitting
                                ? 'Saving…'
                                : model === 'DEDRONE'
                                  ? 'Connect'
                                  : 'Register'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Success views ────────────────────────────────────────────────────────────

function SuccessView({
  result,
  onClose,
  onCopy,
  copiedField,
  onGoToSensor,
}: {
  result: CreatedResult;
  onClose: () => void;
  onCopy: (value: string, field: string) => void;
  copiedField: string | null;
  onGoToSensor: (id: string) => void;
}) {
  if (result.kind === 'dedrone') {
    const { integration } = result;
    return (
      <>
        <div className="flex items-center justify-between mb-5">
          <Dialog.Title className="text-lg font-semibold text-white">
            DeDrone Integration Created
          </Dialog.Title>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-navy-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-5">
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-yellow-200 space-y-1">
              <p>
                In <strong>DedroneTracker.AI</strong> (cloud): go to{' '}
                <strong>OPTIONS → Connectors → Add → HTTP Push</strong> and paste the
                webhook URL below as the target. On older on-prem DroneTracker versions,
                the same setting lives under Configuration → Notifications → HTTP.
              </p>
              <p className="opacity-80">
                Sub-sensors will auto-discover on the next poll.
              </p>
            </div>
          </div>
          <CopyableRow label="Webhook URL" value={integration.webhookUrl} field="webhookUrl" onCopy={onCopy} copiedField={copiedField} />
          <CopyableRow label="Integration ID" value={integration.id} field="integrationId" onCopy={onCopy} copiedField={copiedField} mono />
          <div className="flex justify-end pt-2 border-t border-navy-700/50">
            <button onClick={onClose} className="btn-primary">Done</button>
          </div>
        </div>
      </>
    );
  }

  // Sensor (Dronetag or Generic RID)
  const s = result.sensor;
  const sensorId = s?.sensor?.id || s?.id;
  return (
    <>
      <div className="flex items-center justify-between mb-5">
        <Dialog.Title className="text-lg font-semibold text-white">Sensor Registered</Dialog.Title>
        <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-navy-700">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="space-y-5">
        {s?.ingestionKey?.fullKey && (
          <div>
            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-200">
                Save this API key now. It will not be shown again.
              </p>
            </div>
            <CopyableRow label="Sensor API Key" value={s.ingestionKey.fullKey} field="apiKey" onCopy={onCopy} copiedField={copiedField} mono accent />
          </div>
        )}
        <CopyableRow label="Sensor ID" value={sensorId} field="sensorId" onCopy={onCopy} copiedField={copiedField} mono />
        {s?.ingestionEndpoints && (
          <div>
            <p className="text-xs font-medium text-gray-400 mb-2">Ingestion Endpoints</p>
            <div className="space-y-2">
              {Object.entries(s.ingestionEndpoints).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 p-2 bg-navy-900 rounded-lg border border-navy-700">
                  <span className="text-[10px] text-gray-500 uppercase w-12">{key}</span>
                  <code className="text-xs text-gray-300 font-mono flex-1 break-all">{value as string}</code>
                  <button
                    onClick={() => onCopy(value as string, key)}
                    className="flex-shrink-0 text-gray-500 hover:text-accent transition-colors"
                  >
                    {copiedField === key ? <CheckIcon className="h-3.5 w-3.5 text-accent" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2 border-t border-navy-700/50">
          <button onClick={onClose} className="btn-secondary">Close</button>
          <button onClick={() => onGoToSensor(sensorId)} className="btn-primary flex items-center gap-2">
            Go to Sensor <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

function CopyableRow({
  label,
  value,
  field,
  onCopy,
  copiedField,
  mono = false,
  accent = false,
}: {
  label: string;
  value: string;
  field: string;
  onCopy: (value: string, field: string) => void;
  copiedField: string | null;
  mono?: boolean;
  accent?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}</label>
      <div className="flex items-center gap-2 p-3 bg-navy-900 rounded-lg border border-navy-700">
        <code
          className={clsx(
            'text-sm font-mono flex-1 break-all select-all',
            accent ? 'text-accent' : 'text-white',
            !mono && 'font-sans',
          )}
        >
          {value}
        </code>
        <button
          onClick={() => onCopy(value, field)}
          className="flex-shrink-0 text-gray-500 hover:text-accent transition-colors"
        >
          {copiedField === field ? (
            <CheckIcon className="h-4 w-4 text-accent" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}
